import { faSync } from '@fortawesome/pro-light-svg-icons'
import { ComponentWithPermissions } from '../../../../../baseComponents/CreateRoutering/types'
import DisplayPrice from '../../../../../baseComponents/Displays/DisplayPrice'
import { CalculateParamsTy } from '../../../../../baseComponents/Displays/DisplayPrice/types'
import { TitleSButtonPermissioned } from '../../../../../baseComponents/Layout/TitleAndRightLayout'
import { ButtonConfigColumnsWithModal } from '../../../../../baseComponents/ModalsLayouts/ModalConfigColumnsTabs'
import { permissionCheck } from '../../../../../baseComponents/PermissionChecker/function'
import TwinIcon from '../../../../../baseComponents/TwinIcon'
import { VirtualTableListingStateLess } from '../../../../../baseComponents/TwinTable/VirtualTableListing'
import { CustomRenderString, CustomRenderBoolean } from '../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { RectangularInput, RectangularInputFormatNumberSmall } from '../../../../../forms/Input'
import { replaceDotsForDecimalSeparator } from '../../../../../utils/globals/numbers'
import useSpecificPricesProduct, { SpecificPricesProductLogicProps, SpecificRulesProductExtended } from '../../../../../utils/hooks/product/useSpecificPriceProduct'
import useConfigColumns from '../../../../../utils/hooks/useConfigColumns'
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'
import { getPreparePricesConfigParam } from '../../../../../utils/reducers/getters'
import DisplayMargin from '../../../../Price/DisplayMargin'
import { displayUnitsIcon, pricesRowRender } from '../../../../Price/DisplayPrice'
import { ProductExtendedTyShort } from '../../../types'


type SpecificPricesProductProps = SpecificPricesProductLogicProps & ComponentWithPermissions & {
    basicData: ProductExtendedTyShort
}

const SpecificPricesProduct: React.FC<SpecificPricesProductProps> = ({ userPermissions, basicData }) => {
    const { data, changePrice, saveData, syncWithParent, changeIsPack, changeUnits, addUnits, deleteUnits } = useSpecificPricesProduct({ ProductId: basicData?.id })
    const calculateParamsDefault = getPreparePricesConfigParam() as CalculateParamsTy
    const { t } = useTwinTranslation()
    const columns: ColumnTableSchema<SpecificRulesProductExtended> = [
        {
            id: 'id',
            dataKey: 'id',
            label: t('id', 'ID'),
            customRender: (parameterValue) => <CustomRenderString value={'T' + parameterValue} />,
            width: 60
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre')
        },
        {
            id: 'units',
            dataKey: 'units',
            label: t('fromU', 'A partir de...'),
            toolTip: t('explanationUnitsPrice', 'Es la cantidad a partir de la cual se aplica un precio. En caso de marcar que es un pack, es el conjunto de unidades que componen el pack.'),
            customRender: (parameterValue, allRowData) => <RectangularInputFormatNumberSmall value={parameterValue ?? 1} readOnly={!permissionCheck(userPermissions, 'update')} onChange={(value) => changeUnits(value, allRowData.rowIndex)} className='w-80' />,
            width: 120,
        },
        {
            id: 'is_pack',
            dataKey: 'is_pack',
            label: t('pack', 'Pack'),
            toolTip: t('explanationPack', 'El pack es un conjunto de unidades que se venden de forma conjunta. Si está marcado, el precio se aplicará sobre el conjunto de unidades.'),
            width: 80,
            customRender: (parameterValue, allRowData) =><CustomRenderBoolean onClick={() => permissionCheck(userPermissions, 'update') && changeIsPack(allRowData.rowIndex)}  value={String(parameterValue) === 'true'} width={50} />,
        },
        {
            id: 'price',
            dataKey: 'price',
            label: t('price', 'Precio'),
            toolTip: t('explanationPriceOrFormula', 'Es el precio o la fórmula por unidad. En el caso de que se trate de un pack, el precio es sobre el conjunto de unidades.'),
            customRender: (parameterValue, allRowData) => <RectangularInput value={replaceDotsForDecimalSeparator(parameterValue ?? allRowData.formula ?? '')} readOnly={!permissionCheck(userPermissions, 'update')} onChange={(value) => changePrice(value, allRowData.rowIndex)}></RectangularInput>
        },
        {
            id: 'calculated_price',
            dataKey: 'calculated_price',
            label: t('finalUPrice', 'Precio u. final'),
            customRender: (_parameterValue, allRowData) => {
                return <DisplayPrice value={allRowData?.calculated_price} CurrencyId={allRowData?.CurrencyId || ''} with_vat={allRowData?.with_vat} calculateParams={{
                    decimals: allRowData?.decimals,
                    rounding: allRowData?.rounding,
                    roundingPriceType: calculateParamsDefault?.roundingPriceType
                }} />
            },
            minWidth: 180
        },
        {
            id: '_old_price',
            dataKey: '_old_price',
            label: t('lastPricePrevious', 'Precio anterior'),
            customRender: (_parameterValue, allRowData) => {
                return <DisplayPrice value={parseInt(_parameterValue)} CurrencyId={allRowData?.CurrencyId || ''} with_vat={allRowData?.with_vat} calculateParams={{
                    decimals: allRowData?.decimals,
                    rounding: allRowData?.rounding,
                    roundingPriceType: calculateParamsDefault?.roundingPriceType
                }} />
            },
            minWidth: 180
        },
        {
            id: 'margin',
            dataKey: 'cost',
            label: t('margin', 'Margen'),
            customRender: (_parameterValue, allRowData) => <DisplayMargin allRowData={allRowData} cost={parseFloat(_parameterValue)} className='text-center w-full' />,
            minWidth: 80,
            hideColumnByDefault: true
        },
        {
            id: 'sync',
            dataKey: 'id',
            label: '',
            simpleLabel: t('restore', 'Restablecer'),
            width: 50,
            customRender: (_parameterValue, allRowData) => {
                return < TwinIcon icon = { faSync } onClick = {() => syncWithParent(allRowData)} className = { allRowData.price === null || allRowData.price === '' ? 'text-green-21' : 'text-red-BA' } />
            },
            havePermission: permissionCheck(userPermissions, 'update'),
        },
        {
            id: 'delete',
            dataKey: 'id',
            label: '',
            simpleLabel: t('deleteLine', 'Eliminar línea'),
            width: 50,
            customRender: (_parameterValue, allRowData) => displayUnitsIcon(allRowData, addUnits, deleteUnits),
            havePermission: permissionCheck(userPermissions, 'update'),
        }
    ]
    const { selectedColumnsAndFilters, setSelectedColumnsAndFilters, memorizedColumns } = useConfigColumns({ columns, name: 'specificPricesProduct' })
    return (
        <TitleSButtonPermissioned title={t('prices', 'Precios')} buttonText={t('save', 'Guardar')} onClick={() => saveData(data)} userPermissions={userPermissions} permission='update'>
            <div className='flex justify-end w-full mb-38'>
                <ButtonConfigColumnsWithModal name='specificPricesProduct' columns={columns} setSelectedColumnsAndFilters={setSelectedColumnsAndFilters} selectedColumnsAndFilters={selectedColumnsAndFilters} haveFiltersColumn={false} setTableConfiguration={() => {}} getTableDataFromStart={() => {}}/>
            </div>
            <div className='flex-auto'>
                <VirtualTableListingStateLess tableData={data} columns={memorizedColumns} setSelectedColumnsAndFilters={setSelectedColumnsAndFilters} rowRenderer={pricesRowRender} name='specificPricesProduct' />
            </div>
        </TitleSButtonPermissioned>
    )
}

export default SpecificPricesProduct